.page {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  gap: var(--sp-7);
  max-width: 900px;
  margin: 0 auto;
  padding: var(--sp-7) var(--sp-6) var(--sp-8);
  background: var(--bg);
  color: var(--text);
}

.header {
  display: flex;
  align-items: center;
  gap: var(--sp-5);
}

.title {
  font-size: var(--fs-xl);
  font-weight: 600;
}

.menuEmail {
  min-width: 0;
  color: var(--text);
  font-size: var(--fs-sm);
  overflow-wrap: anywhere;
  text-align: right;
}

/* Обещание и переключатель периода стоят по центру: это шапка витрины, одна
   на обе карточки, и прижатая к левому краю она читалась бы как подпись к
   левой — к Free. Сноска под карточками отцентрована по той же причине. */
.lede {
  max-width: 60ch;
  margin-inline: auto;
  margin-block: 0;
  color: var(--text-2);
  font-size: var(--fs-lg);
  text-align: center;
}

/* ── Период оплаты ───────────────────────────────────────────────────────── */

/* Сегменты в общей обводке: это один выбор из двух, а не две отдельные кнопки.
   Раздельные кнопки читались бы как «месяц» и «год» — два действия. */
.billing {
  display: inline-flex;
  align-self: center;
  padding: var(--sp-1);
  background: var(--surface-sunken);
  border: var(--bw) solid var(--border);
  border-radius: var(--radius-lg);
}

.billingBtn {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-3) var(--sp-5);
  background: transparent;
  border: var(--bw) solid transparent;
  border-radius: var(--radius);
  color: var(--text-2);
  font-size: var(--fs-md);
  cursor: pointer;
}

.billingOn {
  background: var(--surface);
  border-color: var(--border-strong);
  color: var(--text);
  font-weight: 600;
}

.billingSave {
  color: var(--ok);
  font-size: var(--fs-xs);
  font-weight: 600;
}

/* ── Карточки тарифов ────────────────────────────────────────────────────── */

.cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--sp-6);
}

.card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  padding: var(--sp-7) var(--sp-6);
  background: var(--surface);
  border: var(--bw) solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
  overflow: hidden;
}

/* Платный тариф выделен обводкой, а не заливкой: заливка перекрашивает текст
   и ломает читаемость в половине тем, а обводка одинаково работает во всех. */
.cardPro {
  border-color: var(--brand);
  box-shadow: var(--shadow-lift);
}

/* Полоса по верхней кромке — та же роль, что у подсветки колонки Pro в
   таблице: карточку видно боковым зрением, а обводка в тёмных темах почти
   сливается с рамкой обычной карточки. */
.cardPro::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 3px;
  background: var(--brand);
}

.cardName {
  margin: 0;
  font-size: var(--fs-lg);
  font-weight: 600;
}

.price {
  margin: 0;
  font-size: calc(var(--fs-xl) * 1.5);
  font-weight: 600;
  line-height: 1.1;
}

/* Строка сохраняется даже пустой (у Free там пробел): иначе карточки разъезжаются
   по высоте и заголовки списков перестают стоять на одной линии. */
.priceHint {
  margin: 0;
  min-height: 1.4em;
  color: var(--text-3);
  font-size: var(--fs-sm);
}

.tagline {
  margin: 0;
  color: var(--text-2);
  font-size: var(--fs-md);
}

.highlights {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  margin: var(--sp-3) 0 auto;
  padding: 0;
  list-style: none;
}

/* Название тянется, значение прижато к правому краю: колонка цифр читается
   сверху вниз одним движением — «1, 2, 1, 1, 2» против «10, без лимита». */
.highlight {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--sp-4);
  align-items: baseline;
  font-size: var(--fs-md);
}

.highlightName {
  color: var(--text-2);
}

.highlightValue {
  color: var(--text);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  text-align: right;
  white-space: nowrap;
}

.cta {
  justify-content: center;
  margin-top: var(--sp-5);
}

.ctaNote {
  margin: 0;
  color: var(--text-3);
  font-size: var(--fs-sm);
  text-align: center;
}

/* ── Разделы ─────────────────────────────────────────────────────────────── */

.block {
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
}

.h2 {
  margin: 0;
  font-size: var(--fs-lg);
  font-weight: 600;
}

.blockHint {
  max-width: 60ch;
  margin: calc(var(--sp-4) * -1) 0 0;
  color: var(--text-2);
  font-size: var(--fs-md);
}

/* ── Таблица ─────────────────────────────────────────────────────────────── */

/* Таблица лежит на своей подложке с рамкой: три колонки на голом фоне
   рассыпаются на отдельные строки, а это один связный блок. */
.tableWrap {
  overflow-x: auto;
  padding: 0 var(--sp-5) var(--sp-2);
  background: var(--surface);
  border: var(--bw) solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-md);
}

.thFeature,
.thPlan {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: var(--sp-4) var(--sp-4);
  background: var(--surface);
  border-bottom: var(--bw) solid var(--border-strong);
  color: var(--text-3);
  font-size: var(--fs-xs);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.thFeature {
  text-align: left;
}

.thPlan {
  width: 8.5em;
  text-align: center;
}

.thPro {
  color: var(--brand);
}

/* Подсветка строки под курсором: в трёхколоночной таблице глаз теряет строку
   на полпути к колонке Pro — особенно когда значения короткие. */
.row:hover .tdFeature,
.row:hover .tdPlan {
  background: var(--surface-2);
}

.row:hover .tdPro {
  background: color-mix(in srgb, var(--brand) 12%, var(--surface-2));
}

.sectionCell {
  padding: var(--sp-6) var(--sp-4) var(--sp-3);
  text-align: left;
  vertical-align: bottom;
}

.sectionName {
  display: block;
  color: var(--text);
  font-size: var(--fs-md);
  font-weight: 600;
}

.sectionHint {
  display: block;
  max-width: 60ch;
  margin-top: var(--sp-2);
  color: var(--text-3);
  font-size: var(--fs-sm);
  font-weight: 400;
}

.tdFeature {
  padding: var(--sp-4);
  border-top: var(--bw) solid var(--border);
  color: var(--text-2);
  font-weight: 400;
  text-align: left;
}

.tdPlan {
  padding: var(--sp-4);
  border-top: var(--bw) solid var(--border);
  font-variant-numeric: tabular-nums;
  text-align: center;
  white-space: nowrap;
}

/* Колонка Pro подсвечена по всей высоте: так видно, что это одна колонка, а не
   набор ячеек, и глаз не теряет её на длинной таблице. */
.tdPro,
.thPro {
  background: color-mix(in srgb, var(--brand) 7%, transparent);
}

.valueStrong {
  color: var(--text);
  font-weight: 600;
}

.valueNone {
  color: var(--text-3);
}

/* ── Раскрытие остальных строк ───────────────────────────────────────────── */

/* Ячейка без собственных отступов: щёлкать нужно по всей полосе, а не по
   надписи в её середине — на телефоне это разница между попал и не попал. */
.moreCell {
  padding: 0;
  border-top: var(--bw) solid var(--border-strong);
}

.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-3);
  width: 100%;
  padding: var(--sp-4);
  background: transparent;
  border: 0;
  border-radius: var(--radius);
  color: var(--text-2);
  font-size: var(--fs-sm);
  font-weight: 600;
  cursor: pointer;
}

.more:hover {
  background: var(--surface-2);
  color: var(--text);
}

.chevron {
  flex: 0 0 auto;
  transition: transform 120ms ease;
}

.chevronOpen {
  transform: rotate(180deg);
}

@media (prefers-reduced-motion: reduce) {
  .chevron {
    transition: none;
  }
}

/* ── Разовые покупки ─────────────────────────────────────────────────────── */

.extras {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--sp-5);
  margin: 0;
  padding: 0;
  list-style: none;
}

.extra {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--sp-2) var(--sp-4);
  padding: var(--sp-5);
  background: var(--surface);
  border: var(--bw) solid var(--border);
  border-radius: var(--radius-lg);
}

.extraName {
  font-size: var(--fs-md);
  font-weight: 600;
}

.extraPrice {
  color: var(--text);
  font-size: var(--fs-md);
  font-weight: 600;
  white-space: nowrap;
}

.extraHint {
  grid-column: 1 / -1;
  color: var(--text-2);
  font-size: var(--fs-sm);
}

/* ── В работе ────────────────────────────────────────────────────────────── */

.soon {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-3);
  margin: 0;
  padding: 0;
  list-style: none;
}

/* Пунктир — тот же приём, что у черновиков в планировщике: обещанное, но пока
   не существующее не должно выглядеть как готовая строка тарифа. */
.soonItem {
  padding: var(--sp-3) var(--sp-5);
  background: var(--surface-2);
  border: var(--bw) dashed var(--border-strong);
  border-radius: var(--radius);
  color: var(--text-2);
  font-size: var(--fs-sm);
}

/* ── Вопросы ─────────────────────────────────────────────────────────────── */

.faq {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
  margin: 0;
}

.faqItem {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

.faqQ {
  font-size: var(--fs-md);
  font-weight: 600;
}

.faqA {
  max-width: 70ch;
  margin: 0;
  color: var(--text-2);
  font-size: var(--fs-md);
}

/* ── Подвал ──────────────────────────────────────────────────────────────── */

.footer {
  display: flex;
  justify-content: center;
  padding-top: var(--sp-5);
  border-top: var(--bw) solid var(--border);
}

.back {
  color: var(--text-3);
  font-size: var(--fs-sm);
}

/* ── Узкий экран ─────────────────────────────────────────────────────────── */

@media (max-width: 720px) {
  .cards {
    grid-template-columns: minmax(0, 1fr);
  }

  /* Таблица остаётся таблицей и на телефоне.
     Раньше она разворачивалась в карточки — строка превращалась в три: имя,
     «FREE 1», «PRO 10». На тридцати строках это единственное, что помещалось
     на экран, но сравнением быть переставало: два значения оказывались друг
     под другом, а сравнивают их рядом.
     Теперь строк на виду десять, а колонки значений сжаты до ширины самого
     длинного из них («без лимита»), — три колонки помещаются в 320 px, и
     сравнение остаётся сравнением. Крупный кегль в персонализации может их
     всё-таки не вместить: на этот случай у обёртки остаётся горизонтальная
     прокрутка. */
  .tableWrap {
    padding: 0 var(--sp-4) var(--sp-2);
  }

  .table {
    font-size: var(--fs-sm);
  }

  .thFeature,
  .thPlan,
  .tdFeature,
  .tdPlan {
    padding: var(--sp-3) var(--sp-2);
  }

  /* Фиксированной ширины у колонок значений здесь нет: таблица раздаёт им
     ровно столько, сколько нужно самому длинному значению («без лимита»), а
     остаток забирает название. Задать ширину числом значило бы угадывать её за
     пять языков и за произвольный кегель из персонализации — и промахиваться
     на перенос посреди «без лимита». */
  .thPlan {
    width: auto;
  }

  .sectionCell {
    padding: var(--sp-5) var(--sp-2) var(--sp-2);
  }
}
